import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ApiError } from '../../api/client';
import { useFeather } from '../../shell/useFeather';
import { useModalCountdown } from './useModalCountdown';
import { ArcDialog, ArcPage, ArcPageHeader, ArcPanel, ArcSelect, ArcTabs } from '../../components/arc/primitives/arc-primitives';
import { Button } from '../../components/arc/button/button';
import { Send, Workflow } from 'lucide-react';
import './campaign-wizard-arc.css';
import { EmailBuilder } from '../../mailing/EmailBuilder';
import { applyBlocks, blockIssues, type EmailBlock } from '../../mailing/email-blocks';
import { RONDO_BUTTON_COLORS, RONDO_MAIL_SHELL, rondoDefaultBlocks } from './rondo-mail-shell';
import {
  getSegments, getSenders, getPartners, getSegmentEstimate, createCampaign, updateCampaign, testSendCampaign,
  getCampaignDetail, uploadAssetImage,
  type Segment, type Sender, type Partner, type Channel, type CampaignStatus,
} from '../../api/marketing';

const WZ_CSS = `
.wz-stepper{display:flex;align-items:center;justify-content:center;gap:14px;padding:14px 0;}
.wz-step{display:flex;align-items:center;gap:8px;opacity:.5;transition:opacity .2s;}
.wz-step.active,.wz-step.done{opacity:1;}
.wz-step-num{width:32px;height:32px;border-radius:50%;background:var(--bs-tertiary-bg);color:var(--bs-secondary-color);display:flex;align-items:center;justify-content:center;font-weight:700;border:2px solid var(--bs-border-color);}
.wz-step.active .wz-step-num{background:var(--bs-primary);color:#fff;border-color:var(--bs-primary);}
.wz-step.done .wz-step-num{background:#22c55e;color:#fff;border-color:#22c55e;}
.wz-step-label{font-size:.86rem;font-weight:500;color:var(--bs-body-color);}
.wz-step-line{flex:0 0 60px;height:2px;background:var(--bs-border-color);}
.wz-lbl{font-size:.74rem;color:var(--bs-secondary-color);font-weight:600;margin-bottom:2px;display:flex;align-items:center;gap:6px;flex-wrap:wrap;}
.wz-hint{font-weight:400;font-size:.7rem;color:var(--bs-secondary-color);text-transform:none;letter-spacing:0;}
.wz-hint code{font-size:.68rem;background:var(--bs-tertiary-bg);padding:1px 4px;border-radius:3px;}
.wz-recipients{font-size:.84rem;padding:8px 12px;background:rgba(0,113,227,.06);border:1px solid rgba(0,113,227,.18);color:#0058b3;border-radius:8px;}
.wz-validation{padding:12px 14px;border-radius:8px;font-size:.84rem;}
.wz-validation.ok{background:rgba(52,199,89,.08);border:1px solid rgba(52,199,89,.3);color:#1e7a36;}
.wz-validation.err{background:rgba(200,43,35,.08);border:1px solid rgba(200,43,35,.3);color:#991b1b;}
.wz-validation.warn{background:rgba(255,149,0,.08);border:1px solid rgba(255,149,0,.3);color:#a35a00;}
.wz-validation ul{margin:6px 0 0 0;padding-left:20px;}
.wz-vars-toolbar{display:flex;flex-wrap:wrap;gap:14px;padding:8px 10px;background:var(--bs-tertiary-bg);border:1px solid var(--bs-border-color-translucent);border-radius:8px;align-items:center;}
.wz-vars-label{font-size:.7rem;color:var(--bs-secondary-color);text-transform:uppercase;letter-spacing:.03em;font-weight:600;}
.wz-var-btn{font-family:ui-monospace,Menlo,monospace;font-size:.7rem;padding:3px 8px;border-radius:5px;background:#fff;color:#0058b3;border:1px solid rgba(0,113,227,.25);cursor:pointer;transition:background .12s,color .12s;white-space:nowrap;}
.wz-var-btn:hover{background:#0058b3;color:#fff;border-color:#0058b3;}
.wz-var-btn.special{color:#a35a00;border-color:rgba(255,149,0,.3);}
.wz3-card{max-width:760px;margin:0 auto;}
.campaign-wizard{max-width:1180px;margin:0 auto}.campaign-wizard-head{display:flex;align-items:flex-end;justify-content:space-between;gap:24px;margin-bottom:22px}.campaign-wizard-head h1{margin:3px 0 5px;font-size:38px;line-height:1.08;letter-spacing:-.035em;font-weight:750}.campaign-wizard-head p{margin:0;color:#747d84;font-size:14px}.campaign-wizard .wz3-card{border-radius:20px!important}.campaign-wizard .wz3-card>.card-body{padding:28px!important}.campaign-wizard .wz-stepper{margin:0 auto 26px!important;padding:0;max-width:760px}.campaign-wizard .wz-step-num{width:36px;height:36px;background:#f3f4f4;border-color:#e0e3e5}.campaign-wizard .wz-step.active .wz-step-num{background:#e37921;border-color:#e37921}.campaign-wizard .wz-step.done .wz-step-num{background:#202225;border-color:#202225}.campaign-wizard .wz-step-line{background:#e3e6e8}.campaign-wizard .wz-lbl{margin:0 0 7px;color:#5f6970;font-size:12px;letter-spacing:.045em;text-transform:uppercase}.campaign-wizard .row.g-2{--bs-gutter-x:18px;--bs-gutter-y:18px}.campaign-type-backdrop{position:fixed;inset:0;background:rgba(18,24,29,.48);backdrop-filter:blur(8px);z-index:1050;display:flex;align-items:center;justify-content:center;padding:20px}.campaign-type-dialog{max-width:760px;width:100%;border-radius:22px!important}.campaign-type-dialog .card-header{padding:22px 25px!important}.campaign-type-dialog>.card-body{padding:25px!important}
.wz3-card-head{display:flex;justify-content:space-between;align-items:center;gap:10px;margin-bottom:10px;padding-bottom:10px;border-bottom:1px solid var(--bs-border-color-translucent);flex-wrap:wrap;}
.wz3-card-title{display:flex;align-items:center;gap:8px;font-size:.94rem;font-weight:600;}
.wz-summary dt{font-size:.74rem;color:var(--bs-secondary-color);text-transform:uppercase;letter-spacing:.03em;font-weight:600;margin-top:10px;}
.wz-summary dt:first-child{margin-top:0;}
.wz-summary dd{margin:0 0 2px;font-size:.9rem;}
`;

const VAR_GROUPS: { label: string; vars: { token: string; text: string }[]; cls?: string }[] = [
  { label: 'Oslovení', vars: [
    { token: '[% shared.GREETING %]', text: 'Pozdrav' },
    { token: '[% shared.VOCATIVE %]', text: 'Vokativ' },
    { token: '[% shared.FIRST_NAME %]', text: 'Jméno' },
  ] },
  { label: 'Údaje', vars: [
    { token: '[% shared.LAST_NAME %]', text: 'Příjmení' },
    { token: '[% shared.EMAIL %]', text: 'Email' },
  ] },
  { label: 'Speciální', cls: 'special', vars: [{ token: '[SIGNOUT]', text: '[SIGNOUT]' }] },
];

const COUNTRY_OPTS: { v: string; label: string }[] = [
  { v: 'CZ', label: '🇨🇿 CZ - Česko' },
  { v: 'SK', label: '🇸🇰 SK - Slovensko' },
  { v: 'RO', label: '🇷🇴 RO - Rumunsko' },
];

type Step = 1 | 2 | 3;
const CHANNELS: { key: Channel; label: string; step2: string }[] = [
  { key: 'mail', label: '📧 Mailing', step2: 'HTML obsah' },
  { key: 'notif', label: '🔔 Notifikace (Rondo app)', step2: 'Obsah notifikace' },
  { key: 'modal', label: '🪟 Modal okno (post-login)', step2: 'Obsah modalu' },
  { key: 'sms', label: '💬 SMS (SMSmanager)', step2: 'Text SMS' },
];

const SAMPLE_PERSONALIZATION: Record<string, string> = {
  'shared.VOCATIVE': 'Danieli', 'shared.P1': 'Danieli', 'shared.GREETING': 'Dobrý den',
  'shared.FIRST_NAME': 'Daniel', 'shared.LAST_NAME': 'Trojan', 'shared.EMAIL': 'daniel@example.com',
};
function renderPreview(html: string): string {
  let out = html.replace(/\[%\s*([a-zA-Z0-9_.]+)\s*%\]/g, (_m, k) => SAMPLE_PERSONALIZATION[k] ?? `[${k}]`);
  // [SIGNOUT] je Mailkit URL token (jde do href="[SIGNOUT]"). V href ho nahraď
  // jen URL (jinak vznikne rozbité vnořené <a> a atributy se zobrazí jako text);
  // samostatný [SIGNOUT] (mimo href) vykresli jako odhlašovací odkaz.
  out = out.replace(/href\s*=\s*(["'])\s*\[SIGNOUT\]\s*\1/gi, 'href="#"');
  out = out.replace(/\[SIGNOUT\]/g, '<a href="#">Odhlásit odběr</a>');
  return out;
}

const SPAM_KEYWORDS = [
  'ZDARMA', 'BEZPLATNĚ', 'EXKLUZIVNÍ', 'POSLEDNÍ ŠANCE', 'OMEZENÁ NABÍDKA',
  'KLIKNI HNED', 'NAKUP TEĎ', 'VYHRAJTE', 'VÝHRA ZARUČENA', 'ZARUČENĚ',
  'FREE!!!', 'CLICK NOW', 'BUY NOW', 'LAST CHANCE', 'LIMITED OFFER',
  '100% GUARANTEE', 'WINNER', 'ACT NOW', '$$$', '!!!',
];
interface SpamCat { name: string; details: string[]; score: number; level: 'ok' | 'warn' | 'bad'; }
function spamAudit(subject: string, html: string): { score: number; cats: SpamCat[]; verdict: string; level: 'excellent' | 'ok' | 'risk'; blocked: boolean } {
  const cats: SpamCat[] = [];
  let total = 0;
  const addCat = (name: string, rawScore: number, details: string[]) => {
    total += rawScore;
    const s = Math.round(rawScore * 10) / 10;
    cats.push({ name, score: s, details, level: s === 0 ? 'ok' : s >= 2 ? 'bad' : 'warn' });
  };

  const text = html.replace(/<style[\s\S]*?<\/style>/gi, '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ');
  const spamKw = SPAM_KEYWORDS;
  const found: string[] = [];
  for (const kw of spamKw) {
    const re = new RegExp(kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    const matches = (text.match(re) || []).length + (subject.match(re) || []).length;
    if (matches > 0) found.push(`${kw} (${matches}×)`);
  }
  addCat('Spam keywords', Math.min(3, found.length * 0.5), found.length ? found : ['Žádná spam slova']);

  const subjDetails: string[] = [];
  let subjScore = 0;
  const subjLen = subject.length;
  subjDetails.push(`Délka ${subjLen} znaků`);
  if (subjLen < 20) { subjScore += 0.5; subjDetails.push('Krátký subject (< 20 zn.) - méně informativní'); }
  if (subjLen > 60) { subjScore += 0.5; subjDetails.push('Dlouhý subject (> 60 zn.) - bude oříznutý na mobilu'); }
  const upperLen = subject.replace(/[^A-ZÁ-Ž]/g, '').length;
  const lowerLen = subject.replace(/[^a-zá-ž]/g, '').length;
  if (upperLen > 5 && upperLen > lowerLen) { subjScore += 2; subjDetails.push('SUBJECT JE V CAPS LOCK - silný spam signál'); }
  const exclam = (subject.match(/!/g) || []).length;
  if (exclam >= 3) { subjScore += 1.5; subjDetails.push(`${exclam}× vykřičník v subjectu`); }
  if (subjLen >= 20 && subjLen <= 60 && upperLen <= 5 && exclam < 3) subjDetails.unshift('Subject vypadá OK');
  addCat('Předmět', subjScore, subjDetails);

  const htmlLen = html.length;
  const kb = Math.round((htmlLen / 1024) * 10) / 10;
  const weightDetails = [`Velikost ${kb} KB`];
  let wScore = 0;
  if (htmlLen < 5 * 1024) { wScore += 0.5; weightDetails.push('Pod 5 KB - možná příliš krátký mail'); }
  else if (htmlLen > 100 * 1024 && htmlLen <= 150 * 1024) { wScore += 1.5; weightDetails.push('Nad 100 KB - Gmail ořezává mail ("clipped")'); }
  else if (htmlLen > 150 * 1024) { wScore += 3; weightDetails.push('Nad 150 KB - Gmail/Outlook ořezává, deliverability silně klesá'); }
  else weightDetails.push('Velikost OK (5–100 KB)');
  addCat('HTML velikost', wScore, weightDetails);

  const imgCount = (html.match(/<img\b/gi) || []).length;
  const textLen = text.trim().length;
  const ratioDetails = [`${imgCount} obrázků · ${textLen} znaků textu`];
  let rScore = 0;
  if (imgCount > 0 && textLen < 100) { rScore += 3; ratioDetails.push('Pouze obrázky bez textu - silný spam signál'); }
  else if (imgCount > 5 && textLen < 500) { rScore += 1.5; ratioDetails.push('Hodně obrázků, málo textu'); }
  else if (imgCount > 10 && textLen < 1500) { rScore += 0.5; ratioDetails.push('Vysoký poměr obrázků'); }
  else ratioDetails.push('Poměr obrázek/text OK');
  addCat('Obrázky vs text', rScore, ratioDetails);

  const linkRe = /<a\s+[^>]*href\s*=\s*["']([^"']*)["'][^>]*>/gi;
  const links: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = linkRe.exec(html))) links.push(m[1]!);
  const shortener = ['bit.ly', 't.co', 'tinyurl.com', 'ow.ly', 'goo.gl', 'rebrand.ly'];
  const shortenerHits = links.filter((l) => shortener.some((s) => l.includes(s)));
  const domains = new Set<string>();
  links.forEach((l) => {
    try { domains.add(new URL(l.replace(/\[%[\s\S]*?%\]/g, 'https://x.example').replace('[SIGNOUT]', 'https://x.example')).hostname); } catch { /* relativní/neplatná URL */ }
  });
  const linkDetails = [`${links.length} odkazů · ${domains.size} domén`];
  let lScore = 0;
  if (links.length > 20) { lScore += 1; linkDetails.push('Více než 20 odkazů - spam filtr alert'); }
  else if (links.length > 15) { lScore += 0.5; linkDetails.push('Více odkazů (15+)'); }
  if (shortenerHits.length > 0) { lScore += shortenerHits.length; linkDetails.push(`${shortenerHits.length}× shortener (bit.ly apod.) - špatná deliverability`); }
  if (lScore === 0) linkDetails.push('Odkazy OK');
  addCat('Odkazy', lScore, linkDetails);

  const hasMarkedPreheader = /<span\s+data-preheader[^>]*>\s*\S[\s\S]{0,200}?<\/span>/i.test(html);
  const hasGenericPreheader = /<(?:span|div)[^>]*style\s*=\s*["'][^"']*display\s*:\s*none[^"']*["'][^>]*>[\s\S]{20,200}?<\/(?:span|div)>/i.test(html);
  const hasPreheader = hasMarkedPreheader || hasGenericPreheader;
  addCat('Preheader', hasPreheader ? 0 : 0.5, hasPreheader
    ? ['Skrytý preheader nalezen - bude vidět v inbox preview']
    : ['Chybí skrytý preheader - inbox preview vezme prvních ~80 znaků těla. Doporučujeme přidat <span style="display:none">krátký text</span>.']);

  const score = Math.min(10, Math.round(total * 10) / 10);
  const level: 'excellent' | 'ok' | 'risk' = score <= 3 ? 'excellent' : score <= 6 ? 'ok' : 'risk';
  const verdict = level === 'excellent' ? 'Výborné - pošli klidně' : level === 'ok' ? 'OK - zvažujeme opravu' : 'Vysoké riziko spam filtru';
  return { score, cats, verdict, level, blocked: score > 6 };
}

interface ValidationResult { level: 'ok' | 'warn' | 'err'; messages: string[]; }

function validateHtml(html: string, knownSlugs: string[] = []): ValidationResult {
  const msgs: string[] = [];
  let level: ValidationResult['level'] = 'ok';
  const bump = (l: ValidationResult['level']) => { level = level === 'err' || l === 'err' ? 'err' : 'warn'; };
  if (!html.trim()) return { level: 'err', messages: ['HTML obsah je prázdný.'] };
  const open = (html.match(/\[%/g) ?? []).length;
  const close = (html.match(/%\]/g) ?? []).length;
  if (open !== close) { msgs.push(`Nepárové [% %] značky (${open} otevíracích, ${close} zavíracích).`); level = 'err'; }
  if (!/\[SIGNOUT\]/.test(html)) { msgs.push('Chybí odhlašovací odkaz [SIGNOUT] (povinné pro hromadný mail).'); bump('warn'); }
  if (!/<a\s/i.test(html)) { msgs.push('HTML neobsahuje žádný odkaz <a>.'); bump('warn'); }
  if (!/\[%\s*shared\.(VOCATIVE|P1)\s*-?%\]/i.test(html)) {
    msgs.push('Chybí vokativ [% shared.VOCATIVE %] - bez něj mail neosloví příjemce jménem.'); bump('warn');
  }
  const partnerRe = /\[%\s*shared\.PARTNER_([A-Z0-9_]+)_URL\s*-?%\]/gi;
  const unknown: string[] = [];
  let pm: RegExpExecArray | null;
  while ((pm = partnerRe.exec(html))) {
    const slug = pm[1]!.toLowerCase();
    if (knownSlugs.length && !knownSlugs.includes(slug)) unknown.push(pm[1]!);
  }
  if (unknown.length) {
    msgs.push(`Neplatná partner proměnná: ${[...new Set(unknown)].join(', ')} - vlož ji přes chip s platným partnerem tohoto trhu.`);
    level = 'err';
  }
  if (level === 'ok') msgs.push('HTML je v pořádku - proměnné spárované, vokativ i [SIGNOUT] přítomen, partner proměnné platné.');
  return { level, messages: msgs };
}

// Preheader = skrytý text pro náhled ve schránce. Ukládáme ho jako označený
// skrytý span (data-preheader) - bez DB migrace, čitelný zpět při editaci.
// Vkládá se HNED ZA <body> (u celých HTML dokumentů z Beefree), aby reálně
// fungoval; u fragmentu na začátek. compose = vloží, split = vytáhne + očistí.
const PREHEADER_RE = /<span data-preheader[^>]*>[\s\S]*?<\/span>\s*/i;
function preheaderSpan(text: string): string {
  const esc = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return `<span data-preheader style="display:none!important;visibility:hidden;opacity:0;color:transparent;height:0;width:0;overflow:hidden;mso-hide:all">${esc}</span>`;
}
function composeBody(html: string, preheader: string): string {
  const body = html.replace(PREHEADER_RE, '');
  const ph = preheader.trim();
  if (!ph) return body;
  const span = preheaderSpan(ph);
  if (/<body[^>]*>/i.test(body)) return body.replace(/(<body[^>]*>)/i, `$1\n${span}`);
  return `${span}\n${body}`;
}
function splitPreheader(htmlBody: string): { preheader: string; body: string } {
  const m = htmlBody.match(/<span data-preheader[^>]*>([\s\S]*?)<\/span>/i);
  if (!m) return { preheader: '', body: htmlBody };
  const txt = (m[1] ?? '').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
  return { preheader: txt, body: htmlBody.replace(PREHEADER_RE, '') };
}

type ModalBlock = { id: string; type: 'image' | 'heading' | 'text' | 'button'; text?: string; html?: string; url?: string; color?: string; textColor?: string };
const MODAL_SIZE_PX: Record<'s' | 'm' | 'l', number> = { s: 380, m: 440, l: 560 };
const escA = (s: string) => String(s ?? '').replace(/"/g, '&quot;');
const escT = (s: string) => String(s ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
// Skládá bloky do HTML těla modalu (třídy = SDK runtime styling). [% shared.X %] tokeny
// se NEescapují (dosadí server). Pořadí = D&D pořadí.
function composeModalBody(blocks: ModalBlock[]): string {
  return blocks.map((b) => {
    if (b.type === 'image') return b.url ? `<div class="pf-mo-img"><img src="${escA(b.url)}" alt=""></div>` : '';
    if (b.type === 'heading') return b.text ? `<div class="pf-mo-title">${escT(b.text)}</div>` : '';
    if (b.type === 'text') return b.html ? `<div class="pf-mo-text">${b.html}</div>` : '';
    if (b.type === 'button') return b.text ? `<a class="pf-mo-cta" style="background:${escA(b.color || '#e37921')};color:${escA(b.textColor || '#ffffff')}" href="${escA(b.url || '#')}" target="_blank" rel="noopener">${escT(b.text)}</a>` : '';
    return '';
  }).join('');
}

function RichText({ initial, onChange, onFocus }: { initial: string; onChange: (html: string) => void; onFocus: (insert: (t: string) => void) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (ref.current) ref.current.innerHTML = initial || '<p></p>'; }, []);
  const sync = () => onChange(ref.current?.innerHTML ?? '');
  const cmd = (c: string) => { ref.current?.focus(); document.execCommand(c, false); sync(); };
  return (
    <div>
      <div className="btn-group btn-group-sm mb-1" role="group">
        <button type="button" className="btn btn-outline-secondary py-0 px-2" style={{ fontWeight: 800 }} onMouseDown={(e) => e.preventDefault()} onClick={() => cmd('bold')} title="Tučně">B</button>
        <button type="button" className="btn btn-outline-secondary py-0 px-2" style={{ fontStyle: 'italic' }} onMouseDown={(e) => e.preventDefault()} onClick={() => cmd('italic')} title="Kurzíva">I</button>
        <button type="button" className="btn btn-outline-secondary py-0 px-2" style={{ textDecoration: 'underline' }} onMouseDown={(e) => e.preventDefault()} onClick={() => cmd('underline')} title="Podtržení">U</button>
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning className="form-control form-control-sm" style={{ minHeight: 64 }}
        onInput={sync}
        onFocus={() => onFocus((t) => { ref.current?.focus(); document.execCommand('insertText', false, t); sync(); })} />
    </div>
  );
}

export function CampaignWizard({ onDone, editId }: { onDone: () => void; editId?: number }) {
  const [step, setStep] = useState<Step>(1);
  const [showSpamWords, setShowSpamWords] = useState(false);
  const [country, setCountry] = useState('');
  const [channel, setChannel] = useState<Channel>('mail');
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [preheader, setPreheader] = useState('');
  const [senderId, setSenderId] = useState<number | ''>('');
  const [segmentId, setSegmentId] = useState<number | ''>('');
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [html, setHtml] = useState('<p>[% shared.VOCATIVE %], …</p>\n<p><a href="https://www.rondo.cz/">Otevřít</a></p>\n<p>[SIGNOUT]</p>');
  const [msg, setMsg] = useState('');
  const [smsText, setSmsText] = useState('');
  const [senderIdSms, setSenderIdSms] = useState('Rondo');
  const [modalBlocks, setModalBlocks] = useState<ModalBlock[]>(() => [
    { id: 'b0', type: 'image', url: '' },
    { id: 'b1', type: 'heading', text: '[% shared.VOCATIVE %], máme pro tebe dárek' },
    { id: 'b2', type: 'text', html: '<p>Děkujeme za přízeň. Připravili jsme pro tebe něco navíc.</p>' },
    { id: 'b3', type: 'button', text: 'Otevřít', url: 'https://www.rondo.cz/', color: '#e37921', textColor: '#ffffff' },
  ]);
  const [modalSize, setModalSize] = useState<'s' | 'm' | 'l'>('m');
  const [modalDismiss, setModalDismiss] = useState('Už nezobrazovat');
  const [modalTheme, setModalTheme] = useState<'light' | 'dark'>('light');
  const modalPrevRef = useRef<HTMLDivElement>(null);
  useModalCountdown(modalPrevRef, [modalBlocks, modalSize, modalTheme]);
  const [modalFocus, setModalFocus] = useState<{ id: string; field: 'text' | 'html' | 'url' } | null>(null);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const dragIdx = useRef<number | null>(null);
  const richInsertRef = useRef<((t: string) => void) | null>(null);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [senders, setSenders] = useState<Sender[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [mailTab, setMailTab] = useState<'kod' | 'nahled'>('kod');
  const [mailEditor, setMailEditor] = useState<'builder' | 'html'>(editId ? 'html' : 'builder');
  const [blocks, setBlocks] = useState<EmailBlock[]>(() => rondoDefaultBlocks());
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const testEmailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(testEmail.trim());
  async function sendTest() {
    if (!testEmailValid || !senderId) return;
    setTestSending(true);
    setTestResult(null);
    try {
      const r = await testSendCampaign({ to: testEmail.trim(), html: composeBody(html, preheader), subject, country, senderId: Number(senderId) });
      setTestResult({ ok: r.mode === 'sent', msg: r.note });
    } catch (e) {
      setTestResult({ ok: false, msg: e instanceof ApiError ? e.message : 'Odeslání selhalo' });
    } finally {
      setTestSending(false);
    }
  }
  const [prevDevice, setPrevDevice] = useState<'desktop' | 'mobil'>('desktop');
  const [estimate, setEstimate] = useState<number | null>(null);

  useEffect(() => {
    if (segmentId === '') { setEstimate(null); return; }
    let cancelled = false;
    setEstimate(null);
    getSegmentEstimate(Number(segmentId))
      .then((r) => !cancelled && setEstimate(r.count))
      .catch(() => !cancelled && setEstimate(null));
    return () => { cancelled = true; };
  }, [segmentId]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const htmlRef = useRef<HTMLTextAreaElement>(null);

  useFeather([step, channel, modalBlocks.length]);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  useEffect(() => {
    if (!country) { setSegments([]); setSenders([]); setPartners([]); return; }
    let cancelled = false;
    Promise.all([getSegments(country), getSenders(), getPartners(country)])
      .then(([segs, snd, prt]) => {
        if (cancelled) return;
        setSegments(segs.filter((s) => s.country === country && s.active));
        setSenders(snd.filter((s) => s.active && (!s.country || s.country === country)));
        setPartners(prt);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [country]);

  useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    getCampaignDetail(editId)
      .then((d) => {
        if (cancelled) return;
        setName(d.name ?? '');
        if (d.country) setCountry(d.country);
        if (d.channels?.[0]) setChannel(d.channels[0]);
        setSubject(d.subject ?? '');
        if (d.senderId != null) setSenderId(d.senderId);
        if (d.segmentId != null) setSegmentId(d.segmentId);
        if (d.htmlBody) { const sp = splitPreheader(d.htmlBody); setPreheader(sp.preheader); setHtml(sp.body); }
        const m = (d.meta ?? {}) as {
          builder?: { blocks?: EmailBlock[] };
          sms?: { body?: string; sender?: string }; notif?: { message?: string };
          modal?: { size?: 's' | 'm' | 'l'; theme?: 'light' | 'dark'; dismissText?: string | null; blocks?: ModalBlock[] };
        };
        if (m.builder && Array.isArray(m.builder.blocks)) { setBlocks(m.builder.blocks); setMailEditor('builder'); }
        if (m.sms) { setSmsText(m.sms.body ?? ''); if (m.sms.sender) setSenderIdSms(m.sms.sender); }
        if (m.notif?.message) setMsg(m.notif.message);
        if (m.modal) {
          if (Array.isArray(m.modal.blocks) && m.modal.blocks.length) setModalBlocks(m.modal.blocks);
          setModalSize(m.modal.size === 's' || m.modal.size === 'l' ? m.modal.size : 'm');
          setModalDismiss(m.modal.dismissText ?? '');
          setModalTheme(m.modal.theme === 'dark' ? 'dark' : 'light');
        }
        if (d.scheduledAt) { setScheduleOn(true); setScheduledAt(String(d.scheduledAt).slice(0, 16)); }
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [editId]);

  const step2Label = CHANNELS.find((c) => c.key === channel)?.step2 ?? 'Obsah';
  const canStep1 = !!(country && channel && name.trim() && segmentId !== '' && (channel !== 'mail' || (subject.trim() && senderId !== '')));
  const mailValidated = channel !== 'mail' || validation?.level === 'ok' || validation?.level === 'warn';

  function insertVar(token: string) {
    const ta = htmlRef.current;
    if (!ta) { setHtml((h) => h + token); return; }
    const start = ta.selectionStart ?? html.length;
    const end = ta.selectionEnd ?? html.length;
    const next = html.slice(0, start) + token + html.slice(end);
    setHtml(next);
    setValidation(null);
    requestAnimationFrame(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = start + token.length; });
  }

  function patchBlock(id: string, patch: Partial<ModalBlock>) {
    setModalBlocks((bs) => bs.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  }
  function addBlock(type: ModalBlock['type']) {
    const id = 'b' + Math.random().toString(36).slice(2, 8);
    const init: ModalBlock = type === 'button' ? { id, type, text: 'Otevřít', url: 'https://www.rondo.cz/', color: '#e37921', textColor: '#ffffff' }
      : type === 'image' ? { id, type, url: '' }
      : type === 'heading' ? { id, type, text: 'Nadpis' }
      : { id, type: 'text', html: '<p>Text…</p>' };
    setModalBlocks((bs) => [...bs, init]);
  }
  function removeBlock(id: string) { setModalBlocks((bs) => bs.filter((b) => b.id !== id)); }
  function moveBlock(from: number, to: number) {
    setModalBlocks((bs) => { if (to < 0 || to >= bs.length) return bs; const n = [...bs]; const [x] = n.splice(from, 1); n.splice(to, 0, x!); return n; });
  }
  function insertModalVar(token: string) {
    if (!modalFocus) return;
    if (modalFocus.field === 'html' && richInsertRef.current) { richInsertRef.current(token); return; }
    const { id, field } = modalFocus;
    setModalBlocks((bs) => bs.map((b) => (b.id === id ? { ...b, [field]: String((b as Record<string, unknown>)[field] ?? '') + token } : b)));
  }
  async function uploadBlockImage(id: string, file: File | null) {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setErr('Obrázek max 5 MB'); return; }
    setUploadingId(id); setErr(null);
    try { const { url } = await uploadAssetImage(file); patchBlock(id, { url }); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Nahrání obrázku selhalo'); }
    finally { setUploadingId(null); }
  }

  useEffect(() => {
    if (channel !== 'mail' || mailEditor !== 'builder') return;
    const next = applyBlocks(RONDO_MAIL_SHELL, blocks);
    setHtml(next);
    const issues = blockIssues(blocks);
    setValidation(issues.length ? { level: 'err', messages: issues } : validateHtml(next, partners.map((p) => p.slug.toLowerCase())));
  }, [blocks, mailEditor, channel, partners]);

  async function save(schedule: boolean) {
    setSaving(true); setErr(null);
    try {
      const body = {
        name: name.trim(), type: 'oneshot' as const, channels: [channel], country,
        segmentId: Number(segmentId), senderId: senderId === '' ? null : Number(senderId),
        subject: channel === 'mail' ? subject.trim() : null,
        htmlBody: channel === 'mail' ? composeBody(html, preheader) : null,
        scheduledAt: scheduleOn && scheduledAt ? scheduledAt : null,
        meta:
          channel === 'sms' ? { sms: { body: smsText.trim(), sender: senderIdSms.trim() || 'Rondo' } }
          : channel === 'notif' ? { notif: { message: msg.trim() } }
          : channel === 'mail' && mailEditor === 'builder' ? { builder: { blocks } }
          : channel === 'modal' ? { modal: {
              size: modalSize, theme: modalTheme, dismissText: modalDismiss.trim() || null,
              blocks: modalBlocks, body: composeModalBody(modalBlocks),
            } }
          : null,
      };
      let id: number;
      if (editId) { await updateCampaign(editId, body); id = editId; }
      else { ({ id } = await createCampaign(body)); }
      if (schedule) {
        // Modal bez zvoleného času = okamžité zobrazení → 'running' (materializuje se jako
        // aktivní broadcast modal hned). Ostatní kanály / naplánováno = 'scheduled' (vezme engine).
        const goStatus: CampaignStatus = channel === 'modal' && !(scheduleOn && scheduledAt) ? 'running' : 'scheduled';
        await updateCampaign(id, { status: goStatus });
      }
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Uložení selhalo');
      setSaving(false);
    }
  }

  const steps: { n: Step; label: string }[] = [
    { n: 1, label: 'Základní info' }, { n: 2, label: step2Label }, { n: 3, label: 'Náhled a odeslání' },
  ];

  return (
    <ArcPage className="campaign-wizard rondo-arc-campaign-wizard">
      <style>{WZ_CSS}</style>
      <ArcPageHeader eyebrow="MARKETING · KAMPANĚ" title={editId ? 'Upravit kampaň' : 'Nová kampaň'} description={step === 1 ? 'Nastav publikum a způsob doručení.' : step === 2 ? `Připrav ${step2Label.toLowerCase()} a zkontroluj výsledek.` : 'Poslední kontrola před spuštěním.'} meta={<Button variant="secondary" size="sm" onClick={onDone}>Zrušit</Button>} />

      <div className="campaign-studio">
      <div className="wz-stepper mb-3" aria-label="Postup vytvoření kampaně">
        {steps.map((s, i) => (
          <div key={s.n} style={{ display: 'contents' }}>
            <div className={`wz-step${step === s.n ? ' active' : step > s.n ? ' done' : ''}`}>
              <div className="wz-step-num">{s.n}</div>
              <div className="wz-step-label">{s.label}</div>
            </div>
            {i < steps.length - 1 && <div className="wz-step-line" />}
          </div>
        ))}
      </div>
      <main className="campaign-studio-main">
      {err && <div className="alert alert-danger py-2">{err}</div>}

      {step === 1 && (
        <ArcPanel className="wz3-card campaign-setup"><div className="card-body p-3">
          <div className="campaign-step-head"><span>Krok 1</span><h2>Základ kampaně</h2><p>Nejdřív zvol trh a kanál. Podle nich připravíme dostupné odesílatele a cílové segmenty.</p></div>
          <div className="row g-2">
            <div className="col-12 campaign-choice-block"><label className="wz-lbl">Trh <span className="text-danger">*</span></label><div className="campaign-market-options">
              {COUNTRY_OPTS.map((c) => <button key={c.v} type="button" className={country === c.v ? 'active' : ''} onClick={() => setCountry(c.v)}>{c.label}</button>)}
            </div></div>
            <div className="col-12 campaign-choice-block"><label className="wz-lbl">Kanál <span className="text-danger">*</span></label><div className="campaign-channel-options">
              {CHANNELS.map((c) => <button key={c.key} type="button" className={channel === c.key ? 'active' : ''} onClick={() => { setChannel(c.key); setValidation(null); }}><span>{c.label.split(' ')[0]}</span><strong>{c.label.substring(c.label.indexOf(' ') + 1).replace(/\s*\([^)]*\)/, '')}</strong><small>{c.key === 'mail' ? 'Rozesílka do schránky' : c.key === 'modal' ? 'Po přihlášení na webu' : c.key === 'notif' ? 'Zpráva v aplikaci' : 'Krátká mobilní zpráva'}</small></button>)}
            </div></div>
            {channel === 'mail' && <div className="col-12 campaign-choice-block"><label className="wz-lbl">Editor e-mailu <span className="text-danger">*</span></label><div className="campaign-channel-options">
              <button type="button" className={mailEditor === 'builder' ? 'active' : ''} onClick={() => { setMailEditor('builder'); setValidation(null); }}><span>🧩</span><strong>Drag &amp; Drop</strong><small>Poskládáš e-mail z bloků</small></button>
              <button type="button" className={mailEditor === 'html' ? 'active' : ''} onClick={() => { setMailEditor('html'); setValidation(null); }}><span>🧾</span><strong>HTML</strong><small>Vložíš hotové HTML z Beefree</small></button>
            </div></div>}
            <div className="col-12 campaign-form-divider"><span>Obsah a cílení</span></div>
            <div className="col-12"><label className="wz-lbl">Název kampaně <span className="text-danger">*</span> <span className="wz-hint">interní</span></label>
              <input className="form-control form-control-sm" value={name} onChange={(e) => setName(e.target.value)} placeholder="např. Servisní novinka 06/2026 - CZ" /></div>
            {channel === 'mail' && (
              <>
                <div className="col-12"><label className="wz-lbl">Předmět emailu <span className="text-danger">*</span> <span className="wz-hint">lze použít <code>[% shared.VOCATIVE %]</code></span></label>
                  <input className="form-control form-control-sm" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="např. Danieli, dnes je tady nová nabídka pro vás" /></div>
                <div className="col-12"><label className="wz-lbl">Preheader <span className="wz-hint">skrytý text v náhledu schránky (za předmětem); doporučeno ~40–100 znaků. Vloží se automaticky do emailu.</span></label>
                  <input className="form-control form-control-sm" value={preheader} onChange={(e) => setPreheader(e.target.value)} maxLength={200} placeholder="např. CZE vs JAR dnes večer - tipni si výsledek a hraj o bonus" /></div>
              </>
            )}
            {channel === 'mail' && (
              <div className="col-sm-6"><ArcSelect label="Odesílatel *" value={String(senderId)} onChange={(value) => setSenderId(value === '' ? '' : Number(value))} disabled={!country} options={[{ value: '', label: country ? 'Vyber odesílatele' : 'Nejdřív vyber trh' }, ...senders.map((s) => ({ value: String(s.id), label: `${s.name} (${s.email})${s.provider === 'sendgrid' ? ' · SendGrid' : ''}` }))]} /></div>
            )}
            <div className="col-sm-6"><ArcSelect label="Segment (cílení) *" value={String(segmentId)} onChange={(value) => setSegmentId(value === '' ? '' : Number(value))} disabled={!country} options={[{ value: '', label: country ? 'Vyber segment' : 'Nejdřív vyber trh' }, ...segments.map((s) => ({ value: String(s.id), label: s.name }))]} /></div>
            <div className="col-12"><label className="form-check form-switch d-inline-flex align-items-center gap-2 mb-1" style={{ cursor: 'pointer' }}>
              <input className="form-check-input" type="checkbox" checked={scheduleOn} onChange={(e) => setScheduleOn(e.target.checked)} /><span className="wz-lbl mb-0" style={{ textTransform: 'none' }}>{channel === 'modal' ? 'Zobrazovat od konkrétního data/času' : 'Zvolit datum a čas rozesílky'}</span></label>
              <div className="wz-hint mb-1">Pokud necháš vypnuté, odesílá se hned po dokončení wizardu.</div>
              {scheduleOn && <input type="datetime-local" className="form-control form-control-sm" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />}</div>
            {segmentId !== '' && <div className="col-12"><div className="wz-recipients">Odhad příjemců: <strong>{estimate == null ? 'počítám…' : `${estimate.toLocaleString('cs-CZ')} uživatelů`}</strong> · segment <strong>{segments.find((s) => s.id === segmentId)?.name}</strong></div></div>}
          </div>
          <div className="d-flex justify-content-end mt-3"><Button type="button" size="sm" disabled={!canStep1} onClick={() => setStep(2)}>Pokračovat na {channel === 'mail' && mailEditor === 'html' ? 'HTML' : 'obsah'}</Button></div>
        </div></ArcPanel>
      )}

      {step === 2 && (
        <ArcPanel className="wz3-card campaign-content-panel" style={channel === 'modal' ? { maxWidth: 1040 } : channel === 'mail' && mailEditor === 'builder' ? { maxWidth: 1180 } : undefined}><div className="card-body">
          <div className="campaign-step-head"><span>Krok 2</span><h2>{step2Label}</h2><p>{channel === 'mail' ? 'Vlož obsah, doplň personalizaci a ověř technickou kvalitu.' : channel === 'modal' ? 'Sestav obsah z bloků a sleduj výsledek v živém náhledu.' : 'Připrav sdělení pro vybraný kanál.'}</p></div>
          {channel === 'mail' && mailEditor === 'builder' && (
            <div className="campaign-mail-editor">
              <EmailBuilder upload={async (file) => (await uploadAssetImage(file)).url} blocks={blocks} onChange={setBlocks} colors={RONDO_BUTTON_COLORS} topLabel="Hlavička s logem Rondo" bottomLabel="Patička s odhlášením [SIGNOUT] a varováním 18+" />
              {validation && validation.level !== 'ok' && (
                <div className={`wz-validation ${validation.level} mt-2`}>
                  <ul className="mb-0">{validation.messages.map((m, i) => <li key={i}>{m}</li>)}</ul>
                </div>
              )}
            </div>
          )}
          {channel === 'mail' && mailEditor === 'html' && (
            <div className="campaign-mail-editor">
              <div className="text-muted small mb-2">Připrav HTML v Beefree → Copy HTML → vlož sem. Validátor zkontroluje proměnné a partner URLs.</div>
              <div className="wz-vars-toolbar mb-2">
                {VAR_GROUPS.map((g) => (
                  <span key={g.label} className="d-inline-flex align-items-center gap-1" style={{ flexWrap: 'wrap' }}>
                    <span className="wz-vars-label">{g.label}:</span>
                    {g.vars.map((v) => <button key={v.token} type="button" className={`wz-var-btn ${g.cls ?? ''}`} onClick={() => insertVar(v.token)}>{v.text}</button>)}
                  </span>
                ))}
                {partners.length > 0 && (
                  <span className="d-inline-flex align-items-center gap-1" style={{ flexWrap: 'wrap' }}>
                    <span className="wz-vars-label">Partner URL:</span>
                    {partners.map((p) => <button key={p.id} type="button" className="wz-var-btn" style={{ color: '#5b3f96', borderColor: 'rgba(91,63,150,.25)' }} onClick={() => insertVar(`[% shared.PARTNER_${p.slug.toUpperCase()}_URL %]`)}>{p.slug}</button>)}
                  </span>
                )}
              </div>
              <ArcTabs label="Obsah e-mailu" items={[{ value: 'kod', label: 'HTML kód' }, { value: 'nahled', label: 'Náhled' }]} value={mailTab} onChange={setMailTab} className="campaign-editor-tabs" />
              {mailTab === 'kod' ? (
                <textarea ref={htmlRef} className="form-control form-control-sm" rows={13} value={html} onChange={(e) => { setHtml(e.target.value); setValidation(null); }} style={{ fontFamily: 'ui-monospace,Menlo,monospace', fontSize: '.82rem' }} />
              ) : (
                <iframe title="Náhled emailu" sandbox="allow-same-origin" srcDoc={renderPreview(html)} style={{ width: '100%', height: 360, border: '1px solid var(--bs-border-color)', borderRadius: 8, background: '#fff' }} />
              )}
              <div className="d-flex gap-2 align-items-center my-2">
                <Button type="button" variant="secondary" size="sm" onClick={() => setValidation(validateHtml(html, partners.map((p) => p.slug.toLowerCase())))}>Zvalidovat</Button>
                <span className="text-muted small">{html.length} znaků · {(html.match(/<a\s/gi) ?? []).length} odkazů · {(html.match(/<img\s/gi) ?? []).length} obrázků</span>
              </div>
              {validation && (
                <div className={`wz-validation ${validation.level} mb-2`}>
                  <h6 className="mb-1">{validation.level === 'ok' ? '✓ Validace OK' : validation.level === 'warn' ? '⚠ Upozornění' : '✕ Chyby'}</h6>
                  <ul>{validation.messages.map((m, i) => <li key={i}>{m}</li>)}</ul>
                </div>
              )}
            </div>
          )}
          {channel === 'notif' && (
            <>
              <div className="wz-lbl">Text zprávy <span className="text-danger">*</span> <span className="wz-hint">max 240 znaků</span></div>
              <textarea className="form-control form-control-sm" rows={4} maxLength={240} value={msg} onChange={(e) => setMsg(e.target.value)} />
              <div className="text-muted small mt-1">{msg.length}/240</div>
            </>
          )}
          {channel === 'modal' && (() => {
            const dark = modalTheme === 'dark';
            const sampleBody = composeModalBody(modalBlocks).replace(/\[%\s*shared\.([A-Z0-9_]+)\s*-?%\]/gi, (_m, k: string) => SAMPLE_PERSONALIZATION['shared.' + k] ?? (k.endsWith('_URL') ? '#' : '…'));
            // modal s vlastnim pozadim pres celou plochu - karta nesmi pridavat bily ram
            const prevFull = sampleBody.includes('id="pf-full"');
            const pw = MODAL_SIZE_PX[modalSize];
            const BTYPE: Record<ModalBlock['type'], string> = { image: 'Obrázek', heading: 'Nadpis', text: 'Text', button: 'Tlačítko' };
            return (
            <div className="campaign-modal-builder">
              <div className="campaign-block-list">
                <div className="d-flex align-items-center gap-3 flex-wrap mb-2">
                  <div className="d-flex align-items-center gap-1 campaign-modal-size-options">
                    <span className="wz-lbl mb-0 me-1">Velikost:</span>
                    {(['s', 'm', 'l'] as const).map((s) => (
                      <Button key={s} type="button" size="sm" variant={modalSize === s ? 'primary' : 'secondary'} aria-label={`${s === 's' ? 'Malý' : s === 'm' ? 'Střední' : 'Velký'} modal, ${MODAL_SIZE_PX[s]} px`} onClick={() => setModalSize(s)}>{s === 's' ? 'Malý' : s === 'm' ? 'Střední' : 'Velký'}<span className="campaign-modal-size-dimension"> · {MODAL_SIZE_PX[s]} px</span></Button>
                    ))}
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <ArcSelect label="Motiv" value={modalTheme} onChange={(value) => setModalTheme(value === 'dark' ? 'dark' : 'light')} options={[{ value: 'light', label: 'Světlý' }, { value: 'dark', label: 'Tmavý' }]} className="campaign-theme-select" />
                  </div>
                </div>
                <div className="wz-vars-toolbar mb-2">
                  <span className="wz-hint">{modalFocus ? 'Vlož proměnnou:' : 'Proměnné (klikni do pole bloku):'}</span>
                  {VAR_GROUPS.flatMap((g) => g.vars).map((v) => (
                    <Button key={v.token} type="button" variant="secondary" size="sm" disabled={!modalFocus} title={v.token} onMouseDown={(e) => e.preventDefault()} onClick={() => insertModalVar(v.token)}>{v.text}</Button>
                  ))}
                </div>
                {modalBlocks.map((b, idx) => (
                  <div key={b.id} draggable onDragStart={() => { dragIdx.current = idx; }} onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { if (dragIdx.current != null && dragIdx.current !== idx) moveBlock(dragIdx.current, idx); dragIdx.current = null; }}
                    className="campaign-content-block">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <span className="feather-icon" style={{ cursor: 'grab', color: '#9ca3af' }} title="Přetáhni pro změnu pořadí"><i data-feather="menu" style={{ width: 14, height: 14 }} /></span>
                      <strong className="small">{BTYPE[b.type]}</strong>
                      <div className="ms-auto d-flex gap-1">
                        <Button type="button" variant="secondary" size="sm" disabled={idx === 0} onClick={() => moveBlock(idx, idx - 1)} title="Nahoru">↑</Button>
                        <Button type="button" variant="secondary" size="sm" disabled={idx === modalBlocks.length - 1} onClick={() => moveBlock(idx, idx + 1)} title="Dolů">↓</Button>
                        <Button type="button" variant="danger" size="sm" onClick={() => removeBlock(b.id)} title="Smazat">✕</Button>
                      </div>
                    </div>
                    {b.type === 'image' && (
                      <div className="d-flex align-items-center gap-2">
                        {b.url ? <img src={b.url} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--bs-border-color)' }} /> : null}
                        <input className="form-control form-control-sm" placeholder="URL obrázku nebo nahraj →" value={b.url ?? ''} onChange={(e) => patchBlock(b.id, { url: e.target.value })} />
                        <label className="btn btn-sm btn-outline-secondary mb-0" style={{ whiteSpace: 'nowrap' }}>
                          {uploadingId === b.id ? 'Nahrávám…' : 'Nahrát'}
                          <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden disabled={uploadingId === b.id} onChange={(e) => { void uploadBlockImage(b.id, e.target.files?.[0] ?? null); e.target.value = ''; }} />
                        </label>
                      </div>
                    )}
                    {b.type === 'heading' && (
                      <input className="form-control form-control-sm" placeholder="Nadpis (lze [% shared.VOCATIVE %])" value={b.text ?? ''} onFocus={() => setModalFocus({ id: b.id, field: 'text' })} onChange={(e) => patchBlock(b.id, { text: e.target.value })} />
                    )}
                    {b.type === 'text' && (
                      <RichText initial={b.html ?? ''} onChange={(html) => patchBlock(b.id, { html })}
                        onFocus={(ins) => { setModalFocus({ id: b.id, field: 'html' }); richInsertRef.current = ins; }} />
                    )}
                    {b.type === 'button' && (
                      <div className="row g-2">
                        <div className="col-5"><input className="form-control form-control-sm" placeholder="Text tlačítka" value={b.text ?? ''} onFocus={() => setModalFocus({ id: b.id, field: 'text' })} onChange={(e) => patchBlock(b.id, { text: e.target.value })} /></div>
                        <div className="col-7"><input className="form-control form-control-sm" placeholder="Odkaz (lze [% shared.PARTNER_…_URL %])" value={b.url ?? ''} onFocus={() => setModalFocus({ id: b.id, field: 'url' })} onChange={(e) => patchBlock(b.id, { url: e.target.value })} /></div>
                        <div className="col-12 d-flex align-items-center gap-3 mt-1">
                          <label className="d-flex align-items-center gap-1 mb-0"><span className="wz-hint">Barva tlačítka</span>
                            <input type="color" className="form-control form-control-color form-control-sm p-0" style={{ width: 34, height: 26 }} value={b.color || '#e37921'} onChange={(e) => patchBlock(b.id, { color: e.target.value })} /></label>
                          <Button type="button" variant="secondary" size="sm" title="Rondo oranžová #e37921" onClick={() => patchBlock(b.id, { color: '#e37921' })}>Rondo</Button>
                          <label className="d-flex align-items-center gap-1 mb-0"><span className="wz-hint">Barva textu</span>
                            <input type="color" className="form-control form-control-color form-control-sm p-0" style={{ width: 34, height: 26 }} value={b.textColor || '#ffffff'} onChange={(e) => patchBlock(b.id, { textColor: e.target.value })} /></label>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <div className="d-flex gap-1 flex-wrap mt-1">
                  {(['image', 'heading', 'text', 'button'] as const).map((t) => (
                    <Button key={t} type="button" variant="secondary" size="sm" onClick={() => addBlock(t)}>+ {BTYPE[t]}</Button>
                  ))}
                </div>
                <div className="wz-lbl mt-3">Text „zavřít navždy"</div>
                <input className="form-control form-control-sm" style={{ maxWidth: 280 }} value={modalDismiss} onChange={(e) => setModalDismiss(e.target.value)} placeholder="Už nezobrazovat (volitelné)" />
              </div>
              {/* Živý náhled */}
              <div className="campaign-live-preview" style={{ flexBasis: pw + 44 }}>
                <div className="wz-lbl">Náhled <span className="wz-hint">(1:1 - {pw} px, vždy viditelný)</span></div>
                {/* Náhled 1:1 se SDK runtimem - STEJNÉ px hodnoty (sdk-runtime.ts) */}
                <style>{`.pf-prev,.pf-prev *{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif}.pf-prev{font-size:16px}.pf-prev .pf-mo-img{margin-bottom:16px}.pf-prev .pf-mo-img img{max-width:100%;max-height:200px;border-radius:8px;display:block;margin:0 auto}.pf-prev .pf-mo-title{font-size:19px;font-weight:800;margin-bottom:8px;line-height:1.25}.pf-prev .pf-mo-text{font-size:14px;line-height:1.5;margin-bottom:14px;text-align:left}.pf-prev .pf-mo-cta{display:block;background:#e37921;color:#fff;text-align:center;font-weight:700;font-size:16px;padding:12px 22px;border-radius:10px;text-decoration:none;margin-top:8px}`}</style>
                <div style={{ background: '#6b7280', borderRadius: 12, padding: 20, display: 'flex', justifyContent: 'center' }}>
                  <div className="pf-prev" style={{ width: '100%', maxWidth: pw, borderRadius: 16, position: 'relative', boxShadow: '0 18px 50px rgba(0,0,0,.35)', background: prevFull ? 'transparent' : (dark ? '#171b24' : '#fff'), color: dark ? '#e5e7eb' : '#1f2937', padding: prevFull ? 0 : 24 , overflow: prevFull ? 'hidden' : undefined }}>
                    <button type="button" style={{ position: 'absolute', top: 8, right: 10, border: 'none', background: 'transparent', fontSize: 18, color: '#9ca3af', cursor: 'default' }}>✕</button>
                    <div ref={modalPrevRef} dangerouslySetInnerHTML={{ __html: sampleBody || '<div class="pf-mo-text">Přidej bloky…</div>' }} />
                    {modalDismiss.trim() && <div style={{ marginTop: 12, fontSize: '.78rem', color: '#9ca3af', textDecoration: 'underline', textAlign: 'center' }}>{modalDismiss.trim()}</div>}
                  </div>
                </div>
                <div className="text-muted small mt-2">Takto se zobrazí cílenému uživateli, jakmile přijde na web (žádná fronta).</div>
              </div>
            </div>
            );
          })()}
          {channel === 'sms' && (
            <>
              <div className="wz-lbl">Text SMS <span className="text-danger">*</span> <span className="wz-hint">stručně, max 1-2 segmenty</span></div>
              <textarea className="form-control form-control-sm" rows={3} maxLength={459} value={smsText} onChange={(e) => setSmsText(e.target.value)} />
              <div className="text-muted small mt-1">{smsText.length} znaků · {Math.max(1, Math.ceil(smsText.length / 160))} segment(ů)</div>
              <div className="wz-lbl mt-2">Sender ID <span className="wz-hint">max 11 znaků</span></div>
              <input className="form-control form-control-sm" maxLength={11} value={senderIdSms} onChange={(e) => setSenderIdSms(e.target.value)} style={{ maxWidth: 180 }} />
            </>
          )}
          <div className="campaign-step-actions mt-3">
            <Button type="button" variant="secondary" size="sm" onClick={() => setStep(1)}>Zpět</Button>
            <Button type="button" size="sm" disabled={!mailValidated} onClick={() => setStep(3)}>Pokračovat na náhled</Button>
          </div>
        </div></ArcPanel>
      )}

      {/* KROK 3 */}
      {step === 3 && (
        <ArcPanel className="wz3-card campaign-review-panel"><div className="card-body">
          <div className="campaign-step-head"><span>Krok 3</span><h2>Kontrola a spuštění</h2><p>Zkontroluj cílení, obsah a technickou připravenost kampaně.</p></div>
          <div className="wz3-card-head"><div className="wz3-card-title"><span className="feather-icon"><i data-feather="eye" style={{ width: 16, height: 16 }} /></span> Souhrn kampaně</div></div>
          <dl className="wz-summary mb-3">
            <dt>Název</dt><dd>{name}</dd>
            <dt>Trh</dt><dd>{country}</dd>
            <dt>Kanál</dt><dd>{CHANNELS.find((c) => c.key === channel)?.label}</dd>
            {channel === 'mail' && <><dt>Předmět</dt><dd>{subject}</dd><dt>Odesílatel</dt><dd>{senders.find((s) => s.id === senderId)?.name ?? '-'}</dd></>}
            <dt>Segment</dt><dd>{segments.find((s) => s.id === segmentId)?.name ?? '-'}</dd>
            <dt>{channel === 'modal' ? 'Zobrazení' : 'Čas rozesílky'}</dt><dd>{scheduleOn && scheduledAt ? new Date(scheduledAt).toLocaleString('cs-CZ') : (channel === 'modal' ? 'hned po spuštění (komu se zobrazí)' : 'ihned po spuštění')}</dd>
          </dl>

          {/* Spam audit - jen mail */}
          {channel === 'mail' && (() => {
            const sp = spamAudit(subject, composeBody(html, preheader));
            const barColor = sp.level === 'excellent' ? '#22c55e' : sp.level === 'ok' ? '#f59e0b' : '#ef4444';
            return (
              <ArcPanel className="campaign-review-section mb-3"><div className="card-body">
                <div className="wz3-card-head"><div className="wz3-card-title"><span className="feather-icon"><i data-feather="shield" style={{ width: 16, height: 16 }} /></span> Spam audit</div><span className="wz-hint">{sp.verdict}</span></div>
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div style={{ fontSize: '1.6rem', fontWeight: 700, color: barColor }}>{sp.score}</div>
                  <div style={{ flex: 1, height: 10, background: '#fff', borderRadius: 5, overflow: 'hidden', border: '1px solid rgba(0,0,0,.05)' }}>
                    <div style={{ height: '100%', width: `${Math.min(100, (sp.score / 10) * 100)}%`, background: barColor }} />
                  </div>
                  <div className="small fw-semibold" style={{ minWidth: 140, textAlign: 'right', color: barColor }}>{sp.verdict}</div>
                </div>
                <div className="d-flex flex-column gap-2">
                  {sp.cats.map((c) => {
                    const isKw = c.name === 'Spam keywords';
                    const foundSet = isKw
                      ? new Set(c.details.map((d) => d.replace(/\s*\(\d+×\)\s*$/, '')).filter((d) => d !== 'Žádná spam slova'))
                      : null;
                    return (
                    <div key={c.name} className="d-flex align-items-start gap-2" style={{ padding: '8px 10px', borderRadius: 6, background: 'var(--bs-tertiary-bg)', border: '1px solid var(--bs-border-color-translucent)' }}>
                      <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.74rem', color: '#fff', fontWeight: 700, background: c.level === 'ok' ? '#22c55e' : c.level === 'warn' ? '#f59e0b' : '#dc2626' }}>{c.level === 'ok' ? '✓' : '!'}</span>
                      <div style={{ flex: 1 }}>
                        <div className="small fw-semibold">{c.name}</div>
                        {c.details.map((d, di) => <div key={di} className="text-muted" style={{ fontSize: '.74rem' }}>{d}</div>)}
                        {isKw && (
                          <>
                            <button type="button" className="btn btn-link btn-sm p-0" style={{ fontSize: '.72rem', textDecoration: 'none' }} onClick={() => setShowSpamWords((v) => !v)}>
                              {showSpamWords ? '▾ skrýt kontrolovaná slova' : '▸ co se kontroluje?'}
                            </button>
                            {showSpamWords && (
                              <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {SPAM_KEYWORDS.map((kw) => {
                                  const hit = foundSet?.has(kw);
                                  return <span key={kw} style={{ fontSize: '.68rem', padding: '1px 7px', borderRadius: 999, background: hit ? '#fde2e1' : 'var(--bs-body-bg)', color: hit ? '#b42318' : 'var(--bs-secondary-color)', fontWeight: hit ? 700 : 400, border: `1px solid ${hit ? '#f4b9b5' : 'var(--bs-border-color-translucent)'}` }}>{kw}</span>;
                                })}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      <div className="small fw-semibold" style={{ whiteSpace: 'nowrap', color: c.score > 0 ? '#a35a00' : 'var(--bs-secondary-color)' }}>+{c.score}</div>
                    </div>
                    );
                  })}
                </div>
              </div></ArcPanel>
            );
          })()}

          {/* Náhled emailu - desktop/mobil */}
          {channel === 'mail' && (
            <ArcPanel className="campaign-review-section mb-3"><div className="card-body">
              <div className="wz3-card-head"><div className="wz3-card-title"><span className="feather-icon"><i data-feather="eye" style={{ width: 16, height: 16 }} /></span> Náhled emailu</div>
                <ArcTabs label="Velikost náhledu" items={[{ value: 'desktop', label: 'Desktop' }, { value: 'mobil', label: 'Mobil' }]} value={prevDevice} onChange={setPrevDevice} className="campaign-preview-tabs" />
              </div>
              <div className="d-flex justify-content-center"><iframe title="Náhled" sandbox="allow-same-origin" srcDoc={renderPreview(html)} style={{ width: prevDevice === 'desktop' ? '100%' : 375, height: 420, border: '1px solid var(--bs-border-color)', borderRadius: 8, background: '#fff', transition: 'width .2s' }} /></div>
            </div></ArcPanel>
          )}

          {channel === 'mail' && (
            <ArcPanel className="campaign-review-section mb-3"><div className="card-body py-2 px-3">
              <div className="wz3-card-title mb-2"><span className="feather-icon"><i data-feather="send" style={{ width: 14, height: 14 }} /></span> Test odeslání</div>
              <div className="d-flex gap-2 align-items-center">
                <input className="form-control form-control-sm" style={{ maxWidth: 280 }} placeholder="email pro test" value={testEmail} onChange={(e) => { setTestEmail(e.target.value); setTestResult(null); }} />
                <Button type="button" variant="secondary" size="sm" disabled={!testEmailValid || !senderId || testSending} onClick={sendTest} title={!senderId ? 'Nejdřív vyber odesílatele (krok 1)' : 'Odeslat testovací email'}>{testSending ? 'Odesílám…' : 'Odeslat test'}</Button>
              </div>
              {testResult ? (
                <div className={`small mt-1 ${testResult.ok ? 'text-success' : 'text-danger'}`} style={{ fontSize: '.72rem' }}>{testResult.ok ? '✓ ' : '✕ '}{testResult.msg}</div>
              ) : (
                <div className="text-muted small mt-1" style={{ fontSize: '.7rem' }}>Předmět dostane prefix <code>[TEST]</code>. Personalizace = příjemce dle emailu (vokativ + partner URL).</div>
              )}
            </div></ArcPanel>
          )}

          <div className="campaign-step-actions">
            <Button type="button" variant="secondary" size="sm" onClick={() => setStep(2)} disabled={saving}>Zpět</Button>
            <div className="d-flex gap-2">
              <Button type="button" variant="secondary" size="sm" onClick={() => save(false)} disabled={saving}>Uložit jako koncept</Button>
              <Button type="button" size="sm" onClick={() => save(true)} disabled={saving}>{saving ? 'Ukládám…' : channel === 'modal' ? (scheduleOn && scheduledAt ? 'Naplánovat zobrazení' : 'Spustit zobrazení') : (scheduleOn && scheduledAt ? 'Naplánovat rozesílku' : 'Odeslat ihned')}</Button>
            </div>
          </div>
        </div></ArcPanel>
      )}
      </main>
      </div>
    </ArcPage>
  );
}

// Modal volby typu kampaně (1:1 prod: Jednorázová vs Automatizovaná).
export function CampaignTypeChoice({ onPick, onClose }: { onPick: () => void; onClose: () => void }) {
  return (
    <ArcDialog title="Jak chceš komunikovat?" description="Vyber způsob spuštění. Kanál a publikum nastavíš v dalším kroku." onClose={onClose} size="medium" className="rondo-arc-campaign-type-dialog">
          <div className="campaign-type-list">
            <ArcPanel as="button" className="campaign-type-option" onClick={onPick}><span className="campaign-type-icon" aria-hidden="true"><Send size={18} /></span><span><strong>Jednorázová kampaň</strong><small>Jedno sdělení vybranému publiku, ihned nebo v určený čas.</small></span></ArcPanel>
            <ArcPanel className="campaign-type-option disabled"><span className="campaign-type-icon" aria-hidden="true"><Workflow size={18} /></span><span><strong>Automatizovaný scénář <em>Připravujeme</em></strong><small>Vícekroková komunikace řízená chováním uživatele.</small></span></ArcPanel>
          </div>
    </ArcDialog>
  );
}
